import React, {
  ReactElement,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import ProfileViewModeBg from "../../assets/images&icons/bg-login.jpg";
import { axios } from "../..";
import AuthContext from "../../store/AuthContext";
import { getCookie, setCookie } from "../../utils/cookies";
import jwtDecode from "jwt-decode";
import {
  generateLegacyCookies,
  initializeLegacySessions,
} from "../../utils/legacySession";
import { getAndClearRedirectPath } from "../../utils/logout";
import { clearApplicationCache, redirectAfterLogin } from "../../utils/clearApplicationCache";
import classNames from "classnames";
import { resolveAuthErrorPayload } from "../../utils/authErrorPayload";

function TwoFactorAuth(): ReactElement {
  const { loggedPerson, setIsAuthenticated, set2FAuth, setAuthTransitionActive } =
    useContext(AuthContext);
  const [sentValidation, setSentValidation] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [timer, setTimer] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const hasRequestedCode = useRef<boolean>(false);
  const navigate = useNavigate();

  const requestCode = useCallback(async () => {
    try {
      hasRequestedCode.current = true;
      const pendingLogin = sessionStorage.getItem("pendingLogin");
      const ssoLogin = getCookie("ssoLogin") === "true" ? true : false;
      if (ssoLogin && !loggedPerson) {
        return;
      }
      if (!pendingLogin && !ssoLogin) {
        setErrorMessage("Session expired. Please login again.");
        navigate("/login");
        return;
      }
      let usernameToUse;
      if (pendingLogin) {
        const pendingData = JSON.parse(pendingLogin);
        const { username, fromAcknowledgement } = pendingData;

        usernameToUse =
          fromAcknowledgement && loggedPerson
            ? loggedPerson.firmEmail || loggedPerson.userId
            : username;
      }

      if (ssoLogin) {
        if (!loggedPerson) {
          return;
        }
        usernameToUse = loggedPerson?.firmEmail || loggedPerson?.userId;
      }

      const response = await axios.Get(
        `/auth-2fa-code?userName=${usernameToUse}`,
      );
      if (response && response.data) {
        setSentValidation(true);
        setTimer(30);
      }
    } catch (error) {
      setErrorMessage("Failed to send validation code.");
    }
  }, [navigate, loggedPerson]);

  const validateCode = async () => {
    try {
      const ssoLogin = getCookie("ssoLogin") === "true" ? true : false;

      if (ssoLogin) {
        setSubmitting(true);
        setAuthTransitionActive(true);
        await validateCodeSSO();
        return;
      }
      const pendingLogin = sessionStorage.getItem("pendingLogin");

      if (!pendingLogin) {
        setErrorMessage("Session expired. Please login again.");
        navigate("/login");
        return;
      }

      setSubmitting(true);
      setAuthTransitionActive(true);

      const pendingData = JSON.parse(pendingLogin);
      const { username, password, fromAcknowledgement } = pendingData;
      // If coming from acknowledgement screen, handle differently
      if (fromAcknowledgement) {
        // User is already logged in, just verify the 2FA code
        const usernameToUse = loggedPerson
          ? loggedPerson.firmEmail || loggedPerson.userId
          : username;

        const response = await axios.Post("/auth-2fa-code", {
          userName: usernameToUse,
          verificationCode: validationCode,
          fromAcknowledgement: true,
          // No password needed for already authenticated users
        });

        if (response && response.data) {
          if (response.data.status === "success") {
            setSuccessMessage("Validation successful!");

            // User is already authenticated, just set 2FA as completed
            setCookie("2FAuth", "true", 0);
            set2FAuth(true);
            sessionStorage.removeItem("pendingLogin");
            setCookie("ssoLogin", "false", 0);
            // Navigate to main app
            handleRedirect();
          } else {
            setSubmitting(false);
            setAuthTransitionActive(false);
            setErrorMessage("Invalid verification code. Please try again.");
          }
        } else {
          setSubmitting(false);
          setAuthTransitionActive(false);
          setErrorMessage("No response from server. Please try again.");
        }
      } else {
        // Normal login flow
        const response = await axios.Post("/auth-2fa-code", {
          userName: username,
          verificationCode: validationCode,
          password: password,
        });

        if (response && response.data) {
          if (response.data.status === "success" && response.data.loginData) {
            setSuccessMessage("Validation successful!");

            // Complete the login process with the returned tokens
            const { rememberMe } = pendingData;
            await completeLoginAfter2FA(response.data.loginData, rememberMe);
          } else {
            setSubmitting(false);
            setAuthTransitionActive(false);
            setErrorMessage("Invalid response from server. Please try again.");
          }
        } else {
          setSubmitting(false);
          setAuthTransitionActive(false);
          setErrorMessage("No response from server. Please try again.");
        }
      }
    } catch (error: any | unknown) {
      console.error("2FA validation error:", error);
      setSubmitting(false);
      setAuthTransitionActive(false);
      const resolved = resolveAuthErrorPayload(
        (error as any)?.response?.data ?? (error as any)?.message,
      );
      setErrorMessage(
        resolved.message ||
          (typeof (error as any)?.response?.data === "string"
            ? (error as any).response.data
            : "") ||
          (error as any)?.message ||
          "Validation failed. Please try again.",
      );
    }
  };

  const validateCodeSSO = async () => {
    try {
      if (!loggedPerson) {
        setSubmitting(false);
        setAuthTransitionActive(false);
        setErrorMessage("Logged person not found. Please login again.");
        navigate("/login");
        return;
      }

      const username = loggedPerson.firmEmail || loggedPerson.userId;
      const response = await axios.Post("/auth-2fa-code", {
        userName: username,
        verificationCode: validationCode,
        ssoLogin: true,
      });
      if (response && response.data) {
        if (response.data.status === "success") {
          // Clean up pending login data
          set2FAuth(true);
          sessionStorage.removeItem("pendingLogin");
          setCookie("ssoLogin", "false", 0);
          // Set authentication state
          setIsAuthenticated(true);
          setCookie("2FAuth", "true", 0);
          handleRedirect();
        } else {
          setSubmitting(false);
          setAuthTransitionActive(false);
        }
      } else {
        setSubmitting(false);
        setAuthTransitionActive(false);
      }
    } catch (error) {
      console.error("2FA validation error:", error);
      setSubmitting(false);
      setAuthTransitionActive(false);
    }
  };

  const handleResendCode = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    await requestCode();
  };

  const completeLoginAfter2FA = async (loginData: any, rememberMe: boolean) => {
    try {
      if (!loginData.id_token) {
        throw new Error("No id_token received from server");
      }

      const pendingLogin = sessionStorage.getItem("pendingLogin");
      let username = "";
      if (pendingLogin) {
        const pendingData = JSON.parse(pendingLogin);
        username = pendingData.username || "";
      }

      await clearApplicationCache();

      // Set cookies and session storage similar to regular login
      if (rememberMe) {
        setCookie("token", loginData.id_token, 300);
        setCookie(
          "token_decoded",
          JSON.stringify(jwtDecode(loginData.id_token)),
          300,
        );
      } else {
        setCookie("token", loginData.id_token, 0);
        setCookie(
          "token_decoded",
          JSON.stringify(jwtDecode(loginData.id_token)),
          0,
        );
      }

      sessionStorage.setItem("refresh_token", loginData.refresh_token);
      sessionStorage.setItem("access_token", loginData.access_token);
      sessionStorage.setItem("access_timestamp", Date.now().toString());
      sessionStorage.setItem("username", username);

      const claims = jwtDecode(loginData.id_token) as any;
      sessionStorage.setItem("token_decoded", JSON.stringify(claims));

      // Clean up pending login data
      set2FAuth(true);
      sessionStorage.removeItem("pendingLogin");
      setCookie("ssoLogin", "false", 0);
      // Set authentication state
      setIsAuthenticated(true);
      setCookie("2FAuth", "true", 0);

      const query = window.location.search;
      const equalIndex = query.indexOf("=");
      let legacyUrl: string | null = null;
      if (equalIndex !== -1) {
        const candidateUrl = query.substring(equalIndex + 1);
        if (candidateUrl.toLowerCase().includes(".asp")) {
          legacyUrl = candidateUrl;
        }
      }

      if (legacyUrl) {
        try {
          await generateLegacyCookies();
        } catch (error) {
          console.error("[2FA] ERROR:", error);
        }
        handleRedirect();
        return;
      }

      generateLegacyCookies().catch((error) => {
        console.error("[2FA] ERROR:", error);
      });
      const now = Date.now();
      sessionStorage.setItem("legacySessionsInitialized", now.toString());
      initializeLegacySessions(undefined, 5000);

      handleRedirect();
    } catch (error) {
      console.error("Error completing login after 2FA:", error);
      setSubmitting(false);
      setAuthTransitionActive(false);
      setErrorMessage("Failed to complete login. Please try again.");
    }
  };

  // Form Logic
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    await validateCode();
  };

  const handleValidationCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValidationCode(e.target.value);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const clearMessages = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  // Removed competing useEffect - redirect is now handled by completeLoginAfter2FA

  useEffect(() => {
    const handleGoBack = () => {
      navigate("/login", { replace: true });
    };

    window.addEventListener("popstate", () => {
      handleGoBack();
    });

    return () => {
      window.removeEventListener("popstate", handleGoBack);
    };
  }, []);

  const handleRedirect = () => {
    const query = window.location.search;

    const equalIndex = query.indexOf("=");
    if (equalIndex !== -1) {
      const legacyUrl = query.substring(equalIndex + 1);
      if (legacyUrl.toLowerCase().includes(".asp")) {
        // Asegurar que la URL legacy tiene el token si no lo tiene
        const token = getCookie("token");
        if (token && !legacyUrl.includes("token=")) {
          const separator = legacyUrl.includes("?") ? "&" : "?";
          window.location.href = `${legacyUrl}${separator}token=${encodeURIComponent(token)}`;
        } else {
          window.location.href = legacyUrl;
        }
        return;
      }
    }

    const redirectPath = getAndClearRedirectPath();
    if (redirectPath) {
      redirectAfterLogin(redirectPath);
    } else {
      redirectAfterLogin("/mywsg");
    }
  };

  useEffect(() => {
    if (hasRequestedCode.current) {
      return;
    }
    const pendingLogin = sessionStorage.getItem("pendingLogin");
    const ssoLogin = getCookie("ssoLogin");
    if (!pendingLogin && !ssoLogin) {
      navigate("/login?error=The session has expired. Please login again.");
      return;
    }
    requestCode();
  }, [requestCode, navigate]);

  useEffect(
    function startTimer() {
      if (timer > 0) {
        const interval = setInterval(() => {
          setTimer((prevTimer) => prevTimer - 1);
        }, 1000);
        return () => clearInterval(interval);
      }
    },
    [timer],
  );

  return (
    <>
      <div
        className="absolute w-full min-h-screen"
        style={{
          backgroundImage: `url(${ProfileViewModeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      ></div>
      <div className="w-full min-h-screen justify-center items-center flex self-stretch relative">
        <div className="w-8/12 lg:w-[520px]">
          <div
            className={`flex w-full align-middle justify-center gap-28 px-5 py-4 bg-[#9C3325]`}
          >
            <h2
              className={`text-center text-xl font-sans text-white roboto-font`}
            >
              TWO-FACTOR AUTHENTICATION
            </h2>
          </div>
          <div className="bg-white w-full py-4">
            <form
              className={classNames("text-black py-2 flex flex-col gap-4", {
                "pointer-events-none": submitting,
              })}
              onSubmit={handleSubmit}
            >
              <span className="text-[1.125rem] mb-2 font-sans px-5 ">
                A Two-Factor Authentication code has been sent to your email
                address on file, please enter the code below.
              </span>
              <div className="w-full px-5">
                <input
                  type="text"
                  placeholder="Enter Code"
                  value={validationCode}
                  onChange={handleValidationCodeChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                {errorMessage && (
                  <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
                )}
                {successMessage && (
                  <p className="text-green-500 text-sm mt-2">
                    {successMessage}
                  </p>
                )}
              </div>
              <button
                type="submit"
                className={`cursor-pointer w-11/12 text-white mx-auto flex text-center align-middle justify-center py-4 inter_font bg-[#BC3323] font-semibold text-[1rem]`}
              >
                SUBMIT
              </button>
            </form>
            {sentValidation && timer > 0 && (
              <p className="text-center mt-4">Resend code in {timer} seconds</p>
            )}
            {sentValidation && timer === 0 && (
              <div
                onClick={handleResendCode}
                className="cursor-pointer text-center mt-4 text-blue-500"
              >
                Resend Code
              </div>
            )}
            <div className="text-center mt-4 px-5 text-gray-700">
              *Please allow up to 30 seconds for the email to arrive.
              <br />
              *Please ensure you check your Junk Mail folders for the
              authentication code.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TwoFactorAuth;
